import {
    createContext,
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash/fp';
import { useToggleState } from 'app/lib/hooks';
import { Button, ButtonSize, ButtonVariant } from 'app/components/core/Button';
import { ShowDetailsButton } from 'app/components/features/NotificationHub/ShowDetailsButton.jsx';
import { SnackbarVariant } from './constants';
import { SnackbarAction } from './SnackbarAction.jsx';
import { SnackbarDetailsDialog } from './SnackbarDetailsDialog.jsx';
import * as styles from './Snackbar.css';

const cx = classnames.bind(styles);

const SnackbarContext = createContext();

export const useSnackbarContext = () => useContext(SnackbarContext);

export function Snackbar({
    testId = null,
    className = null,
    id = null,
    variant = SnackbarVariant.DEFAULT,
    autoHideEnabled = false,
    autoHideDelay = 5000,
    onDismiss = noop,
    children = null,
    message = null,
    enableShowDetailsIfTruncated = false,
    ...props
}) {
    const { t } = useTranslation();
    const timeoutId = useRef();
    const classes = cx('base', variant, className);
    const dismissSnackbar = useCallback(() => {
        onDismiss(id);
    }, [onDismiss, id]);

    const [isTruncated, setIsTruncated] = useState(false);
    const [isShowingDetailsDialog, toggleDetailsDialog, , closeDetailsDialog] = useToggleState();

    const handleClickDismiss = useCallback(
        (event) => {
            event.stopPropagation();
            clearTimeout(timeoutId.current);
            closeDetailsDialog();
            onDismiss(id);
        },
        [onDismiss, id, closeDetailsDialog]
    );

    useEffect(() => {
        clearTimeout(timeoutId.current);

        if (autoHideEnabled) {
            timeoutId.current = setTimeout(onDismiss, autoHideDelay, id);
        }

        return () => {
            clearTimeout(timeoutId.current);
        };
    }, [autoHideEnabled, autoHideDelay, onDismiss, id]);

    const context = { variant, dismissSnackbar, setIsTruncated };

    const showDetailsButton = enableShowDetailsIfTruncated && isTruncated;

    const renderShowDetailsButton = useCallback(
        () => (
            <SnackbarAction>
                <ShowDetailsButton id={id} onClick={toggleDetailsDialog} />
            </SnackbarAction>
        ),
        [id, toggleDetailsDialog]
    );

    return (
        <Fragment>
            <SnackbarContext.Provider value={context}>
                <div role="alert" data-testid={testId} className={classes} {...props}>
                    {children}
                    {showDetailsButton && renderShowDetailsButton()}
                    <Button
                        className={styles.dismiss}
                        size={ButtonSize.MEDIUM}
                        variant={ButtonVariant.GHOST}
                        icon="close"
                        label={t('components:Snackbar.dismiss')}
                        onClick={handleClickDismiss}
                    />
                </div>
            </SnackbarContext.Provider>
            {isShowingDetailsDialog && (
                <SnackbarDetailsDialog
                    variant={variant}
                    message={message}
                    onClose={toggleDetailsDialog}
                />
            )}
        </Fragment>
    );
}

Snackbar.propTypes = {
    /**
     * Emit a `data-testid` prop on the root element of the component. Use this
     * to help automated testing tools find this particular component.
     */
    testId: PropTypes.string,

    /**
     * Set additional CSS classes on the component to extend the default styles.
     */
    className: PropTypes.string,

    /**
     * Unique id for the notification.
     */
    id: PropTypes.string,

    /**
     * Snackbar variant. Different variants generally indicate different types of
     * notifications or different severity levels.
     */
    variant: PropTypes.oneOf(Object.values(SnackbarVariant)),

    /**
     * Adds "Show Details"-button to the snackbar if the message is truncated.
     *  When the "Show Details"-Button is pressed, a dialog opens that displays content of `message`.
     */
    enableShowDetailsIfTruncated: PropTypes.bool,

    /**
     * Text we show in the details dialog. Should be the same text as in the notification.
     */
    message: PropTypes.string,

    /**
     * Allow the snackbar notification to be automatically hidden (dismissed)
     * after duration specified by `autoHideDelay`.
     */
    autoHideEnabled: PropTypes.bool,

    /**
     * Number of milliseconds after which to automatically call `onDimiss`. Only
     * takes affect if `autoHideEnabled` is set to true.
     */
    autoHideDelay: PropTypes.number,

    /**
     * Callback invoked when the "dimiss" button is activated, or, when the auto
     * hide timer runs out. Passed a single argument: `id`.
     */
    onDismiss: PropTypes.func,

    /**
     * Snackbar content. Use the `SnackbarMessage` and `SnackbarAction` components
     * to markup your content.
     */
    children: PropTypes.node
};
